import { useEffect, useRef } from 'react'

import { type FormikProps } from 'formik'

import { useFeatureFlag } from '@webapp/hooks'
import {
  parsePercentageOrZero,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import { type ExpenseLineItemFormSchema } from './ExpenseLineItemForm'
import { isPresetTax } from '../ExpenseTaxSelect'

interface UseRecomputeTaxAmountParams {
  values: ExpenseLineItemFormSchema
  setFieldValue: FormikProps<ExpenseLineItemFormSchema>['setFieldValue']
  initialTaxAmount: Nullable<number>
}

export const useRecomputeTaxAmount = ({
  values,
  setFieldValue,
  initialTaxAmount,
}: UseRecomputeTaxAmountParams) => {
  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )

  const isTaxAmountOverridden =
    PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS && values.isTaxAmountOverridden

  const lastComputedTaxAmountRef = useRef<Nullable<number>>(initialTaxAmount)

  const recomputeTaxAmountFromSource = () => {
    // A flat dollar amount is entered directly, so there is nothing to derive.
    if (values.taxType?.id === '$') return

    let percent: Nullable<number> = null
    if (isPresetTax(values.taxType)) {
      percent = parsePercentageOrZero(values.taxType.percentage)
    } else if (values.customTaxPercentage != null) {
      percent = values.customTaxPercentage / 100
    }
    const computedTaxAmount =
      percent == null ? null : round2(values.subtotal * percent)

    // Legacy behaviour: without the flag, an absent percentage source leaves the
    // existing amount untouched rather than clearing it.
    if (computedTaxAmount === null && !PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS) {
      return
    }
    lastComputedTaxAmountRef.current = computedTaxAmount
    if (computedTaxAmount !== values.taxAmount) {
      setFieldValue('taxAmount', computedTaxAmount)
    }
  }

  // Recompute tax amount when tax type changes
  const isInitialSourceRun = useRef(true)
  useEffect(() => {
    if (isInitialSourceRun.current) {
      isInitialSourceRun.current = false
      return
    }
    if (values.isTaxAmountOverridden) {
      setFieldValue('isTaxAmountOverridden', false)
    }
    recomputeTaxAmountFromSource()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.taxType, values.customTaxPercentage])

  // Changing the subtotal clears any manual override and recomputes the amount from the new source.
  const isInitialSubtotalRun = useRef(true)
  useEffect(() => {
    if (isInitialSubtotalRun.current) {
      isInitialSubtotalRun.current = false
      return
    }
    if (isTaxAmountOverridden) return
    recomputeTaxAmountFromSource()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.subtotal])

  // A manual edit away from the rate-derived amount marks the line as overridden.
  useEffect(() => {
    if (!PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS) return
    const hasPercentageSource =
      isPresetTax(values.taxType) || values.customTaxPercentage != null
    if (
      hasPercentageSource &&
      values.taxAmount != null &&
      values.taxAmount !== lastComputedTaxAmountRef.current &&
      !values.isTaxAmountOverridden
    ) {
      setFieldValue('isTaxAmountOverridden', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFieldValue is not referentially stable in Formik 2.x
  }, [values.taxAmount])
}
